import {
  ChangeDetectionStrategy,
  Component,
  computed,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { OrdenesService } from '../../utilidades/ordenes.service';
import {
  CONFIGURACION_FICHA_IMPRIMIR_ETIQUETAS,
  CONFIGURACION_TABLA_IMPRIMIR_ETIQUETAS,
} from './utilidades/imprimir-etiquetas.static';
import { deepCopy } from 'src/app/compartido/utilidades/objetos.utils';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import {
  ErrorDesarrollo,
  ErrorFrontend,
} from 'src/app/compartido/utilidades/error.utils';
import { ResponsiveService } from 'src/app/nucleo/utilidades/responsive.service';
import { SourceTablaOrdenes } from '../../utilidades/source-tabla-ordenes.class';
import { TablaAsincronaComponent } from 'src/app/compartido/tabla-asincrona/tabla-asincrona.component';
import { DialogosService } from 'src/app/nucleo/utilidades/dialogos.service';
import { ActivatedRoute } from '@angular/router';
import { valorNumérico } from 'src/app/compartido/utilidades/valores.utils';
import { obtenerFiltro } from 'src/app/compartido/tabla/utilidades/tabla.utils';
import { FormulariosService } from 'src/app/nucleo/utilidades/formularios.service';
import { Parametros } from 'src/app/compartido/utilidades/compartido.models';
import { AutenticacionService } from 'src/app/nucleo/autenticacion/autenticacion.service';
import { AnaliticasService } from 'src/app/nucleo/utilidades/analiticas.service';
import { TipoCampoFormulario } from 'src/app/compartido/formulario/utilidades/formulario.models';

@Component({
  selector: 'ordenes-imprimir-etiquetas',
  templateUrl: './imprimir-etiquetas.component.html',
  styleUrls: ['./imprimir-etiquetas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ImprimirEtiquetasComponent implements OnInit, OnDestroy {
  /* Suscripción */
  private _suscripcion = new Subscription();

  /* Configuración de la ficha */
  protected configFicha = deepCopy(CONFIGURACION_FICHA_IMPRIMIR_ETIQUETAS);

  /* ID de la organización */
  private _orgId: number | undefined;

  /* Fecha */
  private _fecha: string | undefined;

  /* Tabla de Órdenes */
  protected configTabla = deepCopy(CONFIGURACION_TABLA_IMPRIMIR_ETIQUETAS);
  protected dataSource: SourceTablaOrdenes;
  @ViewChild('tabla') tabla!: TablaAsincronaComponent;

  /* Progreso */
  protected pendientes = signal(0);
  protected generadas = signal(0);
  protected total = computed(() => this.pendientes() + this.generadas());

  /* Estado de carga */
  protected cargando = signal(true);

  constructor(
    private readonly _ordenesService: OrdenesService,
    private readonly _formulariosService: FormulariosService,
    private readonly _analiticasService: AnaliticasService,
    private readonly _alertasService: AlertasService,
    private readonly _dialogosService: DialogosService,
    private readonly _autenticacionService: AutenticacionService,
    private readonly _route: ActivatedRoute,
    protected readonly responsiveService: ResponsiveService,
  ) {
    this.dataSource = new SourceTablaOrdenes(_ordenesService);
  }

  ngOnInit(): void {
    this._configurarTabla();
  }

  ngOnDestroy(): void {
    this._suscripcion.unsubscribe();
  }

  /**
   * Inicializa la configuración de la tabla y los filtros.
   * @author Juan Corral
   */
  private async _configurarTabla(): Promise<void> {
    const filtros = this.configTabla.filtros!;

    // Agregar valorIn a los filtros de la tablas
    this._formulariosService.agregarValorIn(filtros, [
      'organizacion',
      'fecha_preparacion',
      'autor_preparacion',
    ]);

    // Agregar opcionesIn al filtro de autor de preparación
    const filtroAutor = obtenerFiltro(filtros, 'autor_preparacion')!;
    if (filtroAutor.tipo !== TipoCampoFormulario.OPCION_MULTIPLE)
      throw new ErrorDesarrollo(
        'El filtro autor_preparacion debe ser de tipo OPCION_MULTIPLE',
      );
    filtroAutor.opcionesIn = new BehaviorSubject<any[]>([]);

    // Poblar opciones de origen
    await this._formulariosService
      .configurarOpcionesOrigen$(filtros, true, false, true)
      .then((suscripcion: Subscription) => this._suscripcion.add(suscripcion));

    // Escuchar query params
    const params = await firstValueFrom(this._route.queryParams);

    // Poblar organización
    this._orgId = valorNumérico(params['organizacion']);
    if (this._orgId === undefined)
      throw new ErrorFrontend('Organización no proporcionada');
    const filtroOrg = obtenerFiltro(filtros, 'organizacion')!;
    filtroOrg.valorIn!.next(this._orgId);

    // Poblar fecha de preparación
    this._fecha = params['fecha'];
    if (this._fecha === undefined)
      throw new ErrorFrontend('Fecha no proporcionada');
    const filtroFecha = obtenerFiltro(filtros, 'fecha_preparacion')!;
    filtroFecha.valorIn!.next(this._fecha);

    // Agregar opciones de autor de preparación
    const autores = await this._autenticacionService.obtenerUsuarios$({
      organizacion: this._orgId,
      permisos: 'prepare_orden',
    });
    const opciones = [{ valor: NaN, nombre: 'Todos' }];
    for (const autor of autores.results)
      opciones.push({ valor: autor.id, nombre: autor.username });
    filtroAutor.opcionesIn!.next(opciones);

    // Agregar autor de preparación, si corresponde
    const usuario = (await this._autenticacionService.usuario$)!;
    if (opciones.map((o) => o.valor).includes(usuario.id))
      filtroAutor.valorIn!.next(usuario.id);

    // Cargar etiquetas generadas
    this._cargarGeneradas$();
  }

  /**
   * Carga las órdenes con etiquetas generadas.
   * @autor Juan Corral
   */
  private async _cargarGeneradas$(): Promise<void> {
    this.cargando.set(true);

    this._analiticasService
      .obtenerTareasDelDia$(this._fecha!, this._orgId!)
      .then((tareas) => {
        this.pendientes.set(tareas.etiquetas['pendientes']);
        this.generadas.set(tareas.etiquetas['generadas']);
        this.cargando.set(false);
      });
  }

  /**
   * Recarga los datos de la tabla y las etiquetas generadas.
   * @author Juan Corral
   */
  public recargarDatos(): void {
    this.tabla.cargarPagina();
    this._cargarGeneradas$();
  }

  /**
   * Descarga la etiqueta de la orden especificada.
   * @param {number} id - ID de la orden.
   * @autor Juan Corral
   */
  protected descargarEtiqueta(id: number): void {
    this._dialogosService
      .abrirDialogo$(
        `¿Descargar 1 etiqueta?`,
        `Se descargará un archivo PDF con la etiqueta.`,
        'Confirmar',
        'Cancelar',
      )
      .then(async (confirmado: boolean) => {
        if (!confirmado) return;

        this._alertasService.alertar(
          `Generando el PDF, esto puede tardar unos segundos...`,
        );

        const filtros: Parametros = { ids: id };
        this._ordenesService
          .descargarEtiquetas$(filtros)
          .then(() => {
            this._alertasService.alertarExito(
              'El archivo ha sido descargado correctamente',
            );
            this.recargarDatos();
          })
          .catch((error: ErrorFrontend) => {
            this._alertasService.alertarError('Error: ' + error.message);
            this.cargando.set(false);
          });
      });
  }

  /**
   * Descarga las etiquetas del dia.
   * @author Juan Corral
   */
  protected async descargarEtiquetas$(): Promise<void> {
    // Definir cantidad de etiquetas
    const cantidad = this.dataSource.total;
    const s = cantidad > 1 ? 's' : '';
    const orden = cantidad > 1 ? 'órdenes' : 'orden';
    if (cantidad === 0) {
      this._alertasService.alertarError(
        'No hay etiquetas para imprimir, intenta cambiar los filtros seleccionados.',
      );
      return;
    }

    // Abrir dialogo de confirmación
    this._dialogosService
      .abrirDialogo$(
        `¿${cantidad < 80 ? 'Descargar' : 'Enviar'} ${cantidad} etiqueta${s}?`,
        `Se ${cantidad < 80 ? 'descargará' : 'enviará por correo'} un archivo PDF con la${s} etiqueta${s} de la${s} ${orden} en la tabla.`,
        'Confirmar',
        'Cancelar',
      )
      .then((confirmado: boolean) => {
        if (!confirmado) return;

        this._alertasService.alertar(
          'Generando el PDF, esto puede tardar unos segundos...',
        );
        const filtros: Parametros = this.tabla.valoresFiltros;

        if (cantidad < 80) {
          this._ordenesService
            .descargarEtiquetas$(filtros)
            .then(() => {
              this._alertasService.alertarExito(
                'El archivo ha sido descargado correctamente',
              );
              this.recargarDatos();
            })
            .catch((error: ErrorFrontend) =>
              this._alertasService.alertarError('Error: ' + error.message),
            );
        } else {
          this._ordenesService
            .enviarEtiquetas$(filtros)
            .then(() =>
              this._alertasService.alertarExito(
                'El archivo será enviado por correo electrónico',
              ),
            )
            .catch((error: ErrorFrontend) =>
              this._alertasService.alertarError('Error: ' + error.message),
            );
        }
      });
  }

  /**
   * Descarga las órdenes  del dia.
   * @author Juan Corral
   */
  protected async descargarOrdenes$(): Promise<void> {
    // Definir cantidad de etiquetas
    const cantidad = this.dataSource.total;
    const s = cantidad > 1 ? 's' : '';
    const orden = cantidad > 1 ? 'órdenes' : 'orden';
    if (cantidad === 0) {
      this._alertasService.alertarError(
        'No órdenes para descargar, intenta cambiar los filtros seleccionados.',
      );
      return;
    }

    // Abrir dialogo de confirmación
    this._dialogosService
      .abrirDialogo$(
        `¿Descargar ${cantidad} ${orden}?`,
        `Se descargará un archivo Excel con la${s} ${orden} de la tabla.`,
        'Confirmar',
        'Cancelar',
      )
      .then((confirmado: boolean) => {
        if (!confirmado) return;

        this._alertasService.alertar(
          'Generando el PDF, esto puede tardar unos segundos...',
        );
        const filtros: Parametros = this.tabla.valoresFiltros;

        this._ordenesService
          .descargarOrdenes$(filtros)
          .then(() => {
            this._alertasService.alertarExito(
              'El archivo ha sido descargado correctamente',
            );
            this.recargarDatos();
          })
          .catch((error: ErrorFrontend) =>
            this._alertasService.alertarError('Error: ' + error.message),
          );
      });
  }
}
